import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clubsApi, transferRequestsApi, marketApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search,
  Shield,
  Users,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  X,
  Coins,
  AlertCircle,
  Loader2,
  Crown,
  Star,
} from 'lucide-react';

const FindClub = () => {
  const { t, i18n } = useTranslation();
  const { user, isCaptain } = useAuth();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClub, setSelectedClub] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [transferCost, setTransferCost] = useState(null);
  const [marketStatus, setMarketStatus] = useState({ is_open: true });
  // Roster preview: when the user clicks on a club CARD (anywhere outside the
  // "Send Request" button), we open a roster dialog showing all the players.
  const [rosterClub, setRosterClub] = useState(null);
  const [rosterMembers, setRosterMembers] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  const isItalian = i18n.language === 'it';

  // Players with club can view but not send requests
  const canSendRequest = !isCaptain && !user?.club_id;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clubsRes, requestsRes, costRes, marketRes] = await Promise.all([
          clubsApi.list(),
          transferRequestsApi.getMyRequests(),
          canSendRequest ? transferRequestsApi.getCost() : Promise.resolve({ data: null }),
          marketApi.getStatus(),
        ]);
        setClubs(clubsRes.data);
        setTransferCost(costRes.data);
        setMyRequests(requestsRes.data);
        setMarketStatus(marketRes.data || { is_open: true });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredClubs = clubs.filter((club) =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPendingRequestForClub = (clubId) => {
    return myRequests.find((r) => r.club_id === clubId && r.status === 'pending');
  };

  const handleSendRequest = async () => {
    if (!selectedClub) return;
    setSendingRequest(true);

    try {
      const res = await transferRequestsApi.create({
        club_id: selectedClub.club_id,
        message: requestMessage || null,
      });
      setMyRequests((prev) => [res.data, ...prev]);
      setSelectedClub(null);
      setRequestMessage('');
      toast.success(isItalian ? 'Richiesta inviata!' : 'Request sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isItalian ? 'Errore invio richiesta' : 'Failed to send request'));
    } finally {
      setSendingRequest(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await transferRequestsApi.cancel(requestId);
      setMyRequests((prev) => prev.filter((r) => r.request_id !== requestId));
      toast.success(isItalian ? 'Richiesta annullata' : 'Request cancelled');
    } catch (error) {
      toast.error(isItalian ? 'Errore annullamento' : 'Failed to cancel');
    }
  };

  // Open the roster dialog for a given club. Fetches the player list lazily.
  const openRoster = async (club) => {
    setRosterClub(club);
    setRosterMembers([]);
    setRosterLoading(true);
    try {
      const res = await clubsApi.getMembers(club.club_id);
      setRosterMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isItalian ? 'Errore caricamento roster' : 'Failed to load roster'));
    } finally {
      setRosterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#00F0FF] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Market Closed Banner */}
      {!marketStatus.is_open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#FF1744]/10 border border-[#FF1744]/30  p-4"
        >
          <div className="flex items-center gap-3">
            <AlertCircle size={24} className="text-[#FF1744] flex-shrink-0" />
            <div>
              <p className="font-bold text-[#FF1744] uppercase tracking-wider text-sm">
                {isItalian ? 'Mercato Chiuso' : 'Market Closed'}
              </p>
              <p className="text-sm text-slate-400">
                {isItalian 
                  ? 'Il mercato trasferimenti è attualmente chiuso. Non è possibile inviare richieste di ingresso.'
                  : 'The transfer market is currently closed. You cannot send join requests.'}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <Search size={28} className="text-[#00F0FF]" />
        <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
          {isItalian ? 'Trova Club' : 'Find Club'}
        </h1>
      </motion.div>

      <Tabs defaultValue="clubs" className="space-y-6">
        <TabsList className="bg-[#0a0d15] border border-white/10 p-1">
          <TabsTrigger
            value="clubs"
            className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
            data-testid="tab-clubs"
          >
            <Shield size={14} className="mr-2" />
            {isItalian ? 'Club Disponibili' : 'Available Clubs'}
          </TabsTrigger>
          <TabsTrigger
            value="requests"
            className="data-[state=active]:bg-[#00F0FF] data-[state=active]:text-white uppercase tracking-wider text-xs font-bold"
            data-testid="tab-my-requests"
          >
            <Send size={14} className="mr-2" />
            {isItalian ? 'Le Mie Richieste' : 'My Requests'} ({myRequests.filter(r => r.status === 'pending').length})
          </TabsTrigger>
        </TabsList>

        {/* Available Clubs */}
        <TabsContent value="clubs">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <Input
              placeholder={isItalian ? 'Cerca club...' : 'Search clubs...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0a0d15] border-white/10 text-white "
              data-testid="search-clubs"
            />
          </div>

          {/* Club Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClubs.map((club) => {
              const pendingRequest = getPendingRequestForClub(club.club_id);
              return (
                <motion.div
                  key={club.club_id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-[#0a0d15] border border-white/10  p-4 hover:border-[#00F0FF]/50 transition-colors card-hover cursor-pointer"
                  onClick={() => openRoster(club)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRoster(club); } }}
                  data-testid={`club-card-${club.club_id}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div
                      className="w-16 h-16 rounded border border-white/10 overflow-hidden flex-shrink-0"
                      style={{ backgroundColor: club.colors?.primary || '#2979FF' }}
                    >
                      {club.logo ? (
                        <img 
                          src={club.logo} 
                          alt={club.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-white/80"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></div>';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Shield size={32} className="text-white/80" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-lg font-semibold text-white truncate">
                        {club.name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-slate-400 text-sm">
                        <Users size={14} />
                        <span>{club.players?.length || 0} {isItalian ? 'giocatori' : 'players'}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[#FFEA00] text-sm">
                        <Coins size={14} />
                        <span>{club.coins?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {pendingRequest ? (
                    <div className="flex items-center justify-between bg-[#FFEA00]/10 border border-[#FFEA00]/30  px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 text-[#FFEA00]">
                        <Clock size={16} />
                        <span className="text-sm">{isItalian ? 'In attesa' : 'Pending'}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleCancelRequest(pendingRequest.request_id); }}
                        className="text-slate-400 hover:text-[#FF1744]"
                        data-testid={`cancel-request-${club.club_id}`}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ) : canSendRequest ? (
                    <Button
                      className="w-full btn-primary"
                      onClick={(e) => { e.stopPropagation(); setSelectedClub(club); }}
                      data-testid={`request-join-${club.club_id}`}
                      disabled={!marketStatus.is_open}
                    >
                      <Send size={16} className="mr-2" />
                      {isItalian ? 'Invia Richiesta' : 'Send Request'}
                    </Button>
                  ) : !marketStatus.is_open ? (
                    <div className="text-center text-sm text-[#FF1744] py-2 bg-[#FF1744]/10  border border-[#FF1744]/30">
                      {isItalian ? 'Il mercato è chiuso' : 'The market is closed'}
                    </div>
                  ) : (
                    <div className="text-center text-sm text-slate-500 py-2">
                      {isItalian ? 'Hai già un club' : 'You already have a club'}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {filteredClubs.length === 0 && (
            <div className="text-center py-12 bg-[#0a0d15] border border-white/10 ">
              <Shield size={48} className="mx-auto text-slate-600 mb-4" />
              <p className="text-slate-500">{isItalian ? 'Nessun club trovato' : 'No clubs found'}</p>
            </div>
          )}
        </TabsContent>

        {/* My Requests */}
        <TabsContent value="requests">
          <div className="space-y-4">
            {myRequests.map((req) => {
              // Find the club data to get the logo
              const clubData = clubs.find(c => c.club_id === req.club_id);
              return (
              <motion.div
                key={req.request_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0a0d15] border border-white/10  p-4 flex items-center justify-between"
                data-testid={`request-${req.request_id}`}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded border border-white/10 overflow-hidden"
                    style={{ backgroundColor: clubData?.colors?.primary || '#2979FF' }}
                  >
                    {clubData?.logo ? (
                      <img 
                        src={clubData.logo} 
                        alt={req.club_name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Shield size={24} className="text-white/80" />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">{req.club_name}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(req.created_at).toLocaleDateString()}
                    </p>
                    {req.message && (
                      <p className="text-sm text-slate-400 mt-1">"{req.message}"</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 text-xs rounded uppercase tracking-wider flex items-center gap-1 ${
                      req.status === 'pending'
                        ? 'bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30'
                        : req.status === 'accepted'
                        ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30'
                        : 'bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30'
                    }`}
                  >
                    {req.status === 'pending' && <Clock size={12} />}
                    {req.status === 'accepted' && <CheckCircle size={12} />}
                    {req.status === 'rejected' && <XCircle size={12} />}
                    {req.status === 'pending' ? (isItalian ? 'In attesa' : 'Pending') :
                     req.status === 'accepted' ? (isItalian ? 'Accettata' : 'Accepted') :
                     (isItalian ? 'Rifiutata' : 'Rejected')}
                  </span>
                  {req.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCancelRequest(req.request_id)}
                      className="text-slate-400 hover:text-[#FF1744]"
                      data-testid={`cancel-${req.request_id}`}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              </motion.div>
              );
            })}
            {myRequests.length === 0 && (
              <div className="text-center py-12 bg-[#0a0d15] border border-white/10 ">
                <Send size={48} className="mx-auto text-slate-600 mb-4" />
                <p className="text-slate-500">
                  {isItalian ? 'Non hai inviato nessuna richiesta' : "You haven't sent any requests"}
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Roster Dialog — shows all players of the club when the user clicks
          on a card. Read-only view for non-members. */}
      <Dialog open={!!rosterClub} onOpenChange={(o) => { if (!o) setRosterClub(null); }}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white max-w-md max-h-[85vh] flex flex-col p-0" data-testid="roster-dialog">
          <DialogHeader className="px-5 py-4 border-b border-white/5 shrink-0">
            <DialogTitle className="font-heading text-base uppercase tracking-wider flex items-center gap-2">
              <Users size={16} className="text-[#00F0FF]" />
              {isItalian ? 'Rosa Club' : 'Club Roster'}
            </DialogTitle>
          </DialogHeader>
          {rosterClub && (
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {/* Club summary */}
              <div className="flex items-center gap-3 pb-3 border-b border-white/5">
                <div
                  className="w-14 h-14 rounded border border-white/10 overflow-hidden flex-shrink-0"
                  style={{ backgroundColor: rosterClub.colors?.primary || '#2979FF' }}
                >
                  {rosterClub.logo ? (
                    <img src={rosterClub.logo} alt={rosterClub.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Shield size={24} className="text-white/80" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-base text-white truncate">{rosterClub.name}</p>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Users size={11} />{rosterMembers.length || rosterClub.players?.length || 0} {isItalian ? 'giocatori' : 'players'}</span>
                    {rosterClub.coins != null && (
                      <span className="flex items-center gap-1 text-[#FFEA00]"><Coins size={11} />{rosterClub.coins?.toLocaleString()}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Players list */}
              {rosterLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-slate-400 text-sm">
                  <Loader2 size={16} className="animate-spin" />
                  {isItalian ? 'Caricamento roster…' : 'Loading roster…'}
                </div>
              ) : rosterMembers.length === 0 ? (
                <div className="py-10 text-center text-slate-500 text-sm" data-testid="roster-empty">
                  {isItalian ? 'Nessun giocatore nel roster.' : 'No players in the roster.'}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {rosterMembers.map((m) => {
                    const role = m.is_captain
                      ? { label: isItalian ? 'Capitano' : 'Captain', className: 'bg-[#FFEA00]/20 text-[#FFEA00] border-[#FFEA00]/30', Icon: Crown }
                      : m.is_vice_captain
                        ? { label: isItalian ? 'Vice' : 'Vice', className: 'bg-[#FF00E5]/20 text-[#FF00E5] border-[#FF00E5]/30', Icon: Star }
                        : null;
                    return (
                      <div
                        key={m.user_id}
                        className="flex items-center gap-3 px-2.5 py-2 bg-[#050508] border border-white/5 rounded hover:border-white/10 transition-colors"
                        data-testid={`roster-member-${m.user_id}`}
                      >
                        {m.picture ? (
                          <img src={m.picture} alt={m.gamertag || m.name} className="w-8 h-8 rounded-full object-cover shrink-0" loading="lazy" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs text-slate-300 shrink-0">
                            {(m.gamertag || m.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium truncate">{m.gamertag || m.name || '—'}</p>
                          {(m.playing_position || m.player_role) && (
                            <p className="text-[10px] text-slate-500 truncate">
                              {[m.player_role, m.playing_position].filter(Boolean).join(' · ')}
                            </p>
                          )}
                        </div>
                        {role && (
                          <span className={`shrink-0 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border rounded ${role.className} flex items-center gap-1`}>
                            <role.Icon size={10} />
                            {role.label}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* CTA: open transfer-request dialog directly from the roster */}
              {canSendRequest && !getPendingRequestForClub(rosterClub.club_id) && marketStatus.is_open && (
                <div className="pt-2">
                  <Button
                    className="w-full btn-primary"
                    onClick={() => { setRosterClub(null); setSelectedClub(rosterClub); }}
                    data-testid="roster-send-request-btn"
                  >
                    <Send size={14} className="mr-2" />
                    {isItalian ? 'Invia Richiesta a questo Club' : 'Send Request to this Club'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Request Dialog */}
      <Dialog open={!!selectedClub} onOpenChange={() => setSelectedClub(null)}>
        <DialogContent className="bg-[#0a0d15] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-wider">
              {isItalian ? 'Richiedi Trasferimento' : 'Request Transfer'}
            </DialogTitle>
          </DialogHeader>
          {selectedClub && (
            <div className="space-y-6 mt-4">
              <div className="bg-[#050508] border border-white/10  p-4 flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded overflow-hidden"
                  style={{ backgroundColor: selectedClub.colors?.primary || '#2979FF' }}
                >
                  {selectedClub.logo ? (
                    <img 
                      src={selectedClub.logo} 
                      alt={selectedClub.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Shield size={32} className="text-white/80" />
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-heading text-lg font-semibold text-white">{selectedClub.name}</p>
                  <p className="text-sm text-slate-400">
                    {selectedClub.players?.length || 0} {isItalian ? 'giocatori' : 'players'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300 uppercase tracking-wider text-xs font-bold">
                  {isItalian ? 'Messaggio (opzionale)' : 'Message (optional)'}
                </Label>
                <Textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  className="bg-[#050508] border-white/10 text-white  min-h-[100px]"
                  placeholder={isItalian 
                    ? 'Presentati al capitano...' 
                    : 'Introduce yourself to the captain...'}
                  data-testid="request-message"
                />
              </div>

              <Button
                className="w-full btn-primary"
                onClick={handleSendRequest}
                disabled={sendingRequest}
                data-testid="submit-request"
              >
                {sendingRequest 
                  ? (isItalian ? 'Invio...' : 'Sending...') 
                  : (isItalian ? 'Invia Richiesta' : 'Send Request')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FindClub;
